import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Upload, X, FileText, CheckCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface UploadedDocument {
  name: string;
  path: string;
  uploadedAt: string;
}

const PartnerOnboarding = () => {
  const { user, role, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [profile, setProfile] = useState<{ full_name: string; email: string } | null>(null);
  
  // Form state
  const [initialInvestment, setInitialInvestment] = useState('');
  const [phone, setPhone] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [notes, setNotes] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);

  // Redirect if not logged in or not a partner
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/partner-login');
      } else if (role && role !== 'partner') {
        navigate('/partner-login');
      }
    }
  }, [user, role, isLoading, navigate]);

  // Fetch profile info
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();
      
      if (profileData) {
        setProfile(profileData);
      }
    };
    
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user) return;

    setUploadingFiles(true);

    try {
      const newDocs: UploadedDocument[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}_${Date.now()}_${i}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('partner-documents')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          toast({
            title: "Upload Failed",
            description: `Failed to upload ${file.name}: ${uploadError.message}`,
            variant: "destructive",
          });
          continue;
        }

        newDocs.push({
          name: file.name,
          path: filePath,
          uploadedAt: new Date().toISOString(),
        });
      }

      setUploadedDocuments([...uploadedDocuments, ...newDocs]);
      
      if (newDocs.length > 0) {
        toast({
          title: "Files Uploaded",
          description: `Successfully uploaded ${newDocs.length} document(s).`,
        });
      }
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast({
        title: "Upload Error",
        description: error.message || "An error occurred while uploading files.",
        variant: "destructive",
      });
    } finally {
      setUploadingFiles(false);
      e.target.value = '';
    }
  };

  const removeDocument = async (doc: UploadedDocument) => {
    try {
      const { error } = await supabase.storage
        .from('partner-documents')
        .remove([doc.path]);

      if (error) {
        console.error('Error removing file:', error);
        toast({
          title: "Error",
          description: "Failed to remove the document.",
          variant: "destructive",
        });
        return;
      }

      setUploadedDocuments(uploadedDocuments.filter(d => d.path !== doc.path));
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    const investmentValue = parseFloat(initialInvestment);
    if (!initialInvestment || isNaN(investmentValue) || investmentValue <= 0) {
      toast({
        title: "Invalid Investment Amount",
        description: "Please enter a valid initial investment amount greater than $0.",
        variant: "destructive",
      });
      return;
    }

    if (!phone || !addressLine1 || !city || !state || !postalCode) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in all required contact information fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const investmentAmount = parseFloat(initialInvestment);
      
      // Convert to JSON-compatible format
      const documentsJson = uploadedDocuments.map(doc => ({
        name: doc.name,
        path: doc.path,
        uploadedAt: doc.uploadedAt,
      }));
      
      // Update partner account with onboarding info
      // Set high_water_mark to initial investment - this is the starting point for fee calculations
      const { error } = await supabase
        .from('partner_accounts')
        .update({
          total_invested: investmentAmount,
          current_value: investmentAmount,
          high_water_mark: investmentAmount,
          phone,
          secondary_email: secondaryEmail || null,
          address_line1: addressLine1,
          address_line2: addressLine2 || null,
          city,
          state,
          postal_code: postalCode,
          country,
          notes: notes || null,
          onboarding_documents: documentsJson,
          onboarding_status: 'onboarding_complete',
          last_updated: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Onboarding Complete",
        description: "Your information has been submitted. The fund manager will review and activate your account shortly.",
      });

      navigate('/partner-dashboard');
    } catch (error: any) {
      console.error('Error submitting onboarding:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "An error occurred while submitting your information.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !user) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-delphi-light">
              Welcome{profile?.full_name ? `, ${profile.full_name}` : ''}!
            </h1>
            <p className="text-delphi-light/60 mt-2">
              Complete your profile to activate your partner account.
            </p>
          </div>

          <Card className="bg-delphi-dark/80 border-delphi-gray/20">
            <CardHeader>
              <CardTitle className="text-xl text-delphi-light">Partner Onboarding</CardTitle>
              <CardDescription>
                Please provide your investment details and contact information below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Initial Investment */}
              <div className="space-y-2">
                <Label htmlFor="initialInvestment" className="text-delphi-light">
                  Initial Investment Amount *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-delphi-light/50">$</span>
                  <Input
                    id="initialInvestment"
                    type="number"
                    placeholder="100,000"
                    value={initialInvestment}
                    onChange={(e) => setInitialInvestment(e.target.value)}
                    className="pl-8 bg-delphi-dark text-delphi-light border-delphi-gray/40"
                  />
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-delphi-light/80 border-b border-delphi-gray/20 pb-2">
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-delphi-light">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondaryEmail" className="text-delphi-light">Secondary Email</Label>
                    <Input
                      id="secondaryEmail"
                      type="email"
                      placeholder="alternate@email.com"
                      value={secondaryEmail}
                      onChange={(e) => setSecondaryEmail(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addressLine1" className="text-delphi-light">Address Line 1 *</Label>
                  <Input
                    id="addressLine1"
                    placeholder="123 Main Street"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addressLine2" className="text-delphi-light">Address Line 2</Label>
                  <Input
                    id="addressLine2"
                    placeholder="Suite 100"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-delphi-light">City *</Label>
                    <Input
                      id="city"
                      placeholder="New York"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-delphi-light">State *</Label>
                    <Input
                      id="state"
                      placeholder="NY"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postalCode" className="text-delphi-light">Postal Code *</Label>
                    <Input
                      id="postalCode"
                      placeholder="10001"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-delphi-light">Country</Label>
                    <Input
                      id="country"
                      placeholder="USA"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                    />
                  </div>
                </div>
              </div>

              {/* Documents Upload Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-delphi-light/80 border-b border-delphi-gray/20 pb-2">
                  Onboarding Documents
                </h3>
                <p className="text-sm text-delphi-light/50">
                  Upload any signed agreements or required documents.
                </p>
                
                <div className="border-2 border-dashed border-delphi-gray/30 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="document-upload"
                    multiple
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingFiles}
                  />
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    {uploadingFiles ? (
                      <Loader2 className="w-8 h-8 text-delphi-light/50 animate-spin mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 text-delphi-light/50 mb-2" />
                    )}
                    <span className="text-delphi-light/70">
                      {uploadingFiles ? 'Uploading...' : 'Click to upload documents'}
                    </span>
                    <span className="text-delphi-light/40 text-sm mt-1">
                      PDF, DOC, DOCX, PNG, JPG (max 10MB each)
                    </span>
                  </label>
                </div>

                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    {uploadedDocuments.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-delphi-red" />
                          <span className="text-delphi-light text-sm">{doc.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDocument(doc)}
                          className="text-delphi-light/50 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-delphi-light">Additional Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional information you'd like to share..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-delphi-dark text-delphi-light border-delphi-gray/40 min-h-[80px]"
                />
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                className="w-full bg-delphi-red hover:bg-red-600"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Onboarding
                  </>
                )}
              </Button>

              <p className="text-center text-delphi-light/40 text-sm">
                Once submitted, the fund manager will review and activate your account.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </PageLayout>
  );
};

export default PartnerOnboarding;